import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";
import ContactUsCard from "../ContactUsCard";

export default function ShareYourIdeaRedesign() {
  const location = useLocation();
  const defaultSubject = (location.state && location.state.from) ? location.state.from : "Collaboration Proposal";

  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState("");

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    emailjs
      .sendForm(
        "service_vn3hh7u",
        "template_ohp6ofd",
        form.current,
        "user_97BArShYWL8HlU7aUDVzW"
      )
      .then(
        (result) => {
          console.log(result.text);
          setIsSubmitting(false);
          setSubmitStatus("success");
          form.current.reset();
          setTimeout(() => setSubmitStatus(null), 5000);
        },
        (error) => {
          console.error("EmailJS error:", error);
          setIsSubmitting(false);
          setSubmitStatus("error");
          setErrorMessage(error.text || error.message || JSON.stringify(error) || "Unknown EmailJS error");
        }
      );
  };

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* 1. Page Hero Section */}
      <section className="relative min-h-[35vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Collaborative Innovation
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Share Your Idea
          </h1>
          <p className="max-w-2xl mx-auto text-sm md:text-base text-slate-300 font-light leading-relaxed">
            Reducing the burden of healthcare requires shared thoughts. We provide a state-of-the-art platform to convert your ideas into stable, scalable formulations.
          </p>
        </div>
      </section>

      {/* 2. Main Description and Form Section */}
      <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Collaboration Framework */}
          <div className="lg:col-span-5 space-y-8 text-left">
            <div className="space-y-4">
              <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full">
                Ecosystem Partners
              </div>
              <h2 className="text-3xl font-bold text-brand-blue tracking-tight leading-tight">
                An Idea Can Change a Life
              </h2>
              <p className="text-slate-500 font-light leading-relaxed text-sm md:text-base">
                Our collaboration with research institutes, renowned academicians, and budding scientists across the globe helps bring the best thoughts into commercial reality. We support NCE companies to develop First-in-Human formulations and encourage scientists to generate valuable IPs.
              </p>
            </div>

            {/* Bullet points grid */}
            <div className="space-y-5">
              <div className="flex items-start space-x-4">
                <div className="w-9 h-9 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider">Confidentiality & IP Protection</h4>
                  <p className="text-xs text-slate-500 font-light mt-0.5 leading-relaxed">
                    We maintain strict NDA standards and transparent legal agreements to protect your intellectual property at every phase of development.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-9 h-9 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider">Rapid Prototyping & PKPD</h4>
                  <p className="text-xs text-slate-500 font-light mt-0.5 leading-relaxed">
                    Leverage our PKPD and analytical validation teams to develop stable, robust process designs matching regulatory requirements quickly.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-9 h-9 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-brand-blue text-sm uppercase tracking-wider">ViRACS Innovation Center</h4>
                  <p className="text-xs text-slate-500 font-light mt-0.5 leading-relaxed">
                    Access state-of-the-art facilities and equipment under our Business Automation Platform, enabling shared efforts and cost-tracking.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Submission Form */}
          <div className="lg:col-span-7 bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-lg relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-brand-blue to-brand-teal" />
            
            <div className="mb-6 space-y-1 text-left">
              <h3 className="text-2xl font-bold text-brand-blue tracking-tight">Collaboration Form</h3>
              <p className="text-slate-500 font-light text-sm">
                Provide details about your project or formulation idea. Your data will be sent securely to our research coordinator.
              </p>
            </div>

            {/* Success / Failure Banner notifications */}
            {submitStatus === "success" && (
              <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm flex items-start space-x-2.5 animate-fadeIn">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-left">
                  <span className="font-semibold block">Idea Submitted!</span>
                  Your proposal has been successfully sent. Our R&D coordinator will contact you shortly.
                </div>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start space-x-2.5 animate-fadeIn">
                <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-left">
                  <span className="font-semibold block">Submission Failed</span>
                  There was a problem sending your proposal: <span className="font-medium">{errorMessage}</span>. Please try again or email us directly.
                </div>
              </div>
            )}

            {/* Form */}
            <form ref={form} onSubmit={sendEmail} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-1.5 text-left">
                  <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Your Name</label>
                  <input 
                    type="text" 
                    name="from_name"
                    required
                    placeholder="Full Name"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                  />
                </div>
                <div className="space-y-1.5 text-left">
                  <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Organization / University</label>
                  <input 
                    type="text" 
                    name="user_organization"
                    placeholder="Institution Name"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-1.5 text-left">
                  <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Email Address</label>
                  <input 
                    type="email" 
                    name="user_email"
                    required
                    placeholder="name@institution.com"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                  />
                </div>
                <div className="space-y-1.5 text-left">
                  <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Contact Number</label>
                  <input 
                    type="tel" 
                    name="user_Mo_Number"
                    pattern="[0-9]{10}"
                    maxLength="10"
                    placeholder="10-Digit Mobile Number"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1.5 text-left">
                <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Subject / Proposal Area</label>
                <input 
                  type="text" 
                  name="idea_subject"
                  required
                  defaultValue={defaultSubject}
                  placeholder="e.g. Matrix Formulation Idea"
                  className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none"
                />
              </div>

              <div className="space-y-1.5 text-left">
                <label className="block text-xs font-bold text-brand-blue uppercase tracking-wider">Idea Description</label>
                <textarea 
                  name="message"
                  required
                  rows="5"
                  placeholder="Describe your innovation, key goals, and proposed collaboration support needed..."
                  className="w-full bg-slate-50 border border-slate-200 focus:border-brand-teal focus:bg-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-brand-teal/20 transition-all outline-none resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-brand-blue hover:bg-brand-blue/90 text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2 ${
                  isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Submitting Proposal...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Idea</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>

        </div>
      </section>

      {/* 3. Global Contact Us Card Accent */}
      <ContactUsCard sender="Genr" />
    </div>
  );
}
